/// <reference types="vite/client" />
import {
  HeadContent,
  Outlet,
  Scripts,
  createRootRoute,
  useRouterState,
} from "@tanstack/react-router";
import * as React from "react";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { DefaultCatchBoundary, clearReloadMarker } from "~/components/DefaultCatchBoundary";
import { NotFound } from "~/components/NotFound";
import { Banner } from "~/components/layout/Banner";
import { Header } from "~/components/layout/Header";
import { Footer } from "~/components/layout/Footer";
import { PrivacyNotice } from "~/components/layout/PrivacyNotice";
import appCss from "~/styles/app.css?url";
import { seo, seoLinks, websiteJsonLd, organizationJsonLd } from "~/utils/seo";
import { captureUtmParams } from "~/utils/utm";
import { hasMinimalChrome } from "~/config/navigation";

const GTM_ID = "GTM-P7JJRX8";
const HUBSPOT_PORTAL_ID = "9161697";
const RB2B_KEY = "Y4O7Z0HR0MNX";

export const Route = createRootRoute({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "theme-color", content: "#6421ff" },
      ...seo({
        title: "C1.ai | Access for the agentic era",
        description:
          "C1 is the identity security platform that helps security, IT, and GRC teams secure every identity with access reviews, access requests, and lifecycle management.",
      }),
    ],
    links: [
      // --- Preconnect hints first — these must appear before any requests to
      //     the same origins so the browser can start TCP/TLS handshakes ASAP.
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      {
        rel: "preconnect",
        href: "https://fonts.gstatic.com",
        crossOrigin: "anonymous",
      },
      // dns-prefetch fallbacks for browsers that don't support preconnect
      { rel: "dns-prefetch", href: "https://fonts.googleapis.com" },
      { rel: "dns-prefetch", href: "https://fonts.gstatic.com" },

      // Preload the self-hosted display font used by the hero heading (Anybody)
      // so it arrives before first paint and the heading doesn't swap/reflow.
      {
        rel: "preload",
        href: "/fonts/Anybody-Variable-Latin.woff2",
        as: "font",
        type: "font/woff2",
        crossOrigin: "anonymous",
      },

      // --- Stylesheets & assets
      { rel: "stylesheet", href: appCss },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&family=JetBrains+Mono:wght@400;700&display=swap",
      },
      {
        rel: "icon",
        href: "/images/favicon-light.svg",
        type: "image/svg+xml",
        media: "(prefers-color-scheme: dark)",
      },
      {
        rel: "icon",
        href: "/images/favicon-dark.svg",
        type: "image/svg+xml",
        media: "(prefers-color-scheme: light)",
      },
      { rel: "icon", href: "/images/favicon.ico", sizes: "48x48" },
      {
        rel: "apple-touch-icon",
        href: "/images/apple-touch-icon.png",
      },
    ],
    scripts: [
      // Site-wide structured data (schema.org). The WebSite node gives crawlers
      // and AI answer engines a stable `#website` anchor; the Organization node
      // is the `#organization` that WebSite.publisher references (knowledge
      // panel, canonical name, logo, social profiles).
      websiteJsonLd(),
      organizationJsonLd(),
      {
        children: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer','${GTM_ID}');`,
      },
      // Prevent GTM tags that use document.write() from destroying the page
      // after load. document.write() after DOMContentLoaded wipes the entire
      // document, causing an infinite reload loop. This converts those calls
      // into safe DOM insertions instead.
      {
        children: `document.addEventListener('DOMContentLoaded',function(){
          var _origWrite=document.write.bind(document);
          document.write=document.writeln=function(s){
            var t=document.createElement('span');
            t.innerHTML=s;
            (document.body||document.documentElement).appendChild(t);
          };
        });`,
      },
    ],
  }),
  errorComponent: DefaultCatchBoundary,
  notFoundComponent: () => <NotFound />,
  component: RootLayout,
  shellComponent: RootDocument,
});

/**
 * Root layout — rendered inside the hydrated React tree.
 * Banner, Header, Footer are here (not in shellComponent) so they
 * get hydrated and their event handlers work (MegaMenu, MobileMenu, etc).
 */
function RootLayout() {
  // Get the matched route's ID (e.g. "/blog/$slug") and convert TanStack's
  // $param syntax to Vercel's [param] syntax for proper route aggregation
  // in the Speed Insights dashboard.
  const routeId = useRouterState({
    select: (s) => {
      const matches = s.matches;
      const leaf = matches[matches.length - 1];
      return leaf?.routeId?.replace(/\$(\w+)/g, "[$1]") ?? null;
    },
  });

  // Persist campaign attribution (utm_* + gclid) for the session. Runs on the
  // initial load and on every client navigation; a URL that carries tracking
  // params overwrites the stored set (last-touch), a UTM-less URL leaves it
  // intact. Consumers (lu.ma CTAs, HubSpot forms) read it back so attribution
  // survives internal navigation between the influencer landing and the
  // eventual register click / form submit.
  const locationHref = useRouterState({ select: (s) => s.location.href });
  React.useEffect(() => {
    captureUtmParams(window.location.search);
  }, [locationHref]);

  // Minimal-chrome pages (e.g. the Transform registration wizard) render
  // their own logo bar + footer and open in a dedicated tab, so the
  // site-wide nav/footer would only add an exit path off a high-intent flow.
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const hideChrome = hasMinimalChrome(pathname);

  // Clear any stale SSR-reload marker once a page has successfully
  // rendered. The marker exists to prevent infinite reload loops on
  // genuinely broken pages, but if rendering succeeds we want a fresh
  // budget so future transient navigation errors can still auto-recover.
  React.useEffect(() => {
    clearReloadMarker();
  }, []);

  return (
    <>
      <SpeedInsights route={routeId} />
      {!hideChrome && <Banner />}
      {!hideChrome && <Header />}
      <main className="relative min-h-screen">
        <div className="relative z-10">
          <Outlet />
        </div>
      </main>
      {!hideChrome && <Footer />}
      <PrivacyNotice />
    </>
  );
}

/**
 * Shell component — the static HTML document wrapper.
 * Only contains <html>, <head>, <body>, and {children}.
 * NOT hydrated — no interactive components should be here.
 */
function RootDocument({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        <noscript>
          <iframe
            src={`https://www.googletagmanager.com/ns.html?id=${GTM_ID}`}
            height="0"
            width="0"
            style={{ display: "none", visibility: "hidden" }}
          />
        </noscript>
        {children}
        <Scripts />
        {/* HubSpot Tracking Code */}
        <script
          type="text/javascript"
          id="hs-script-loader"
          async
          defer
          src={`//js.hs-scripts.com/${HUBSPOT_PORTAL_ID}.js`}
        />
        {/* RB2B — reverse IP-to-company identification */}
        <script
          id="rb2b-loader"
          dangerouslySetInnerHTML={{
            __html: `!function(key){if(window.reb2b)return;window.reb2b={loaded:true};var s=document.createElement("script");s.async=true;s.src="https://b2bjsstore.s3.us-west-2.amazonaws.com/b/"+key+"/"+key+".js.gz";document.getElementsByTagName("script")[0].parentNode.insertBefore(s,document.getElementsByTagName("script")[0]);}("${RB2B_KEY}");`,
          }}
        />
        {/* Chili Piper Chat Widget */}
        <script
          dangerouslySetInnerHTML={{
            __html: `window.ChiliChat={config:{workspaceId:'d2825229-4f24-4b91-872b-59eeaea0a5f7',tenantId:'c1.ai',chatUrl:'https://c1ai.chilipiper.com/chat/widget/',eventsHandler:function(){}}}`,
          }}
        />
        <script
          type="module"
          crossOrigin="anonymous"
          src="https://c1ai.chilipiper.com/chat/embed/assets/index.js"
        />
      </body>
    </html>
  );
}
